import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { Empty, Pagination, Spin, Table as AntTable } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import { UserR } from '../../../services/users';
import { AddOrEditModal } from './AddOrEditModal';
import { UsersTableActions } from './TableAction';

type TableProps = {
  users: UserR[];
  loadingTable: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onEdit: () => void;
  onDeactivate: () => void;
  onActivate: () => void;
  onManageTokens: (userId: number) => void;
  onPaginationChange: (value: number) => void;
};

export const Table = ({
  users,
  loadingTable,
  totalItems,
  currentPage,
  pageSize,
  onEdit,
  onDeactivate,
  onActivate,
  onManageTokens,
  onPaginationChange
}: TableProps): JSX.Element => {
  const [currEditIndex, setCurrEditIndex] = useState(-1);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const closeEditModal = () => {
    setCurrEditIndex(-1);
    setEditModalVisible(false);
  };

  const openEditModal = (index: number) => {
    setCurrEditIndex(index);
    setEditModalVisible(true);
  };

  const columns = [
    {
      title: 'ID #',
      dataIndex: 'id'
    },
    {
      title: 'First name',
      dataIndex: 'firstName'
    },
    {
      title: 'Last name',
      dataIndex: 'lastName'
    },
    {
      title: 'Date created',
      dataIndex: 'createdDatetime',
      align: 'center' as const,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Email',
      dataIndex: 'email'
    },
    {
      title: 'Legal Email',
      dataIndex: 'legalEmail'
    },
    {
      title: 'Active',
      key: 'isActive',
      align: 'center' as const,
      render: (_: any, { isActive }: UserR) => {
        return isActive ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#f81d22" />;
      }
    },
    {
      title: 'Actions',
      width: 150,
      key: 'operation',
      align: 'center' as const,
      render: (_: any, row: UserR, index: number) => {
        return (
          <UsersTableActions
            user={row}
            onEditClick={() => openEditModal(index)}
            onDeactivate={onDeactivate}
            onActivate={onActivate}
            onManageTokens={() => onManageTokens(row.id)}
          />
        );
      }
    }
  ];

  const pagination = (
    <Pagination
      className="ant-table-pagination ant-table-pagination-right"
      total={totalItems}
      current={currentPage}
      onChange={onPaginationChange}
      showSizeChanger={false}
      pageSize={pageSize}
    />
  );

  return (
    <div>
      <div className="hidden md:block">
        <AntTable
          style={{ marginTop: '3%' }}
          loading={loadingTable}
          rowKey={({ id }) => id}
          dataSource={users}
          columns={columns}
          pagination={false}
          scroll={{ x: 1200, scrollToFirstRowOnChange: true }}
          expandRowByClick={true}
          onRow={(_, index) => ({
            onClick: () => openEditModal(index!)
          })}
          footer={() => pagination}
        />
      </div>

      <div className="block md:hidden">
        {loadingTable ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : users.length === 0 ? (
          <div className="rounded-lg border border-grey-border bg-surface py-10">
            <Empty />
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {users.map((user, index) => (
              <li
                key={user.id}
                role="button"
                tabIndex={0}
                onClick={() => openEditModal(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openEditModal(index);
                  }
                }}
                className="cursor-pointer rounded-lg border border-grey-border bg-surface p-4 shadow-sm transition-colors active:bg-grey-bg"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-base font-semibold text-grey-text">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="mt-0.5 text-xs text-grey-secondary">ID #{user.id}</div>
                  </div>
                  <div className="shrink-0 pt-1">
                    {user.isActive ? (
                      <CheckCircleTwoTone twoToneColor="#52c41a" />
                    ) : (
                      <CloseCircleTwoTone twoToneColor="#f81d22" />
                    )}
                  </div>
                </div>

                <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
                  <dt className="text-grey-secondary">Email</dt>
                  <dd className="truncate text-grey-text">{user.email}</dd>

                  <dt className="text-grey-secondary">Legal email</dt>
                  <dd className="truncate text-grey-text">{user.legalEmail ?? '—'}</dd>

                  <dt className="text-grey-secondary">Created</dt>
                  <dd className="text-grey-text">
                    {user.createdDatetime ? dayjs(user.createdDatetime).format('YYYY-MM-DD') : '—'}
                  </dd>
                </dl>

                <div className="mt-3 flex justify-end border-t border-grey-border pt-3">
                  <UsersTableActions
                    user={user}
                    onEditClick={() => openEditModal(index)}
                    onDeactivate={onDeactivate}
                    onActivate={onActivate}
                    onManageTokens={() => onManageTokens(user.id)}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex justify-end">{pagination}</div>
      </div>

      {currEditIndex !== -1 && (
        <AddOrEditModal
          modalVisible={editModalVisible}
          user={users[currEditIndex]}
          onCancel={closeEditModal}
          onSuccess={() => {
            closeEditModal();
            onEdit();
          }}
        />
      )}
    </div>
  );
};
